import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    List,
    ListItem,
    ListItemText,
    IconButton,
    Button,
    CircularProgress,
    Chip,
    Tooltip,
    Card,
    CardContent,
    CardActions,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    DialogContentText,
    Checkbox,
    Menu,
    MenuItem,
    Toolbar,
    alpha
} from '@mui/material';
import {
    CloudUpload as UploadIcon,
    Delete as DeleteIcon,
    Description as FileIcon,
    Refresh as RefreshIcon,
    Visibility as EyeIcon,
    CheckCircle as CompletedIcon,
    Error as ErrorIcon,
    Pending as PendingIcon,
    Sync as ProcessingIcon,
    SmartToy as SmartToyIcon,
    BubbleChart as GraphIcon
} from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import { apiClient } from '@/services/api';
import DocumentUploadDialog from '@/components/upload/DocumentUploadDialog';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';

interface Document {
    id: number;
    document_name: string;
    status: string;
    uploaded_at: string;
    file_size: number;
    // MD转换相关字段
    processing_status?: string;  // pending, processing, completed, failed
    processing_progress?: number;  // 0-100
    processing_error?: string;
    markdown_path?: string;  // 数据库中存储的实际markdown文件路径
    processed_at?: string;
}

const DocumentPanel: React.FC = () => {
    const { projectId: projectIdStr } = useParams<{ projectId: string }>();
    const projectId = projectIdStr ? parseInt(projectIdStr, 10) : null;
    const [error, setError] = useState<string | null>(null);
    const [documents, setDocuments] = useState<Document[]>([]);
    const [loading, setLoading] = useState(false);
    const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
    // Markdown查看模态框状态
    const [markdownModalOpen, setMarkdownModalOpen] = useState(false);
    const [markdownContent, setMarkdownContent] = useState<string>('');
    const [markdownTitle, setMarkdownTitle] = useState<string>('');
    const [loadingMarkdown, setLoadingMarkdown] = useState(false);
    // 图数据库可视化状态
    const [graphVisualizationDialogOpen, setGraphVisualizationDialogOpen] = useState<boolean>(false);
    const [graphVisualizationHtml, setGraphVisualizationHtml] = useState<string>('');
    const [loadingGraphVisualization, setLoadingGraphVisualization] = useState<boolean>(false);

    const [kgStatus, setKgStatus] = useState<{
        status: string;
        progress: number;
        total_documents: number;
        processed_documents: number;
    } | null>(null);

    const fetchDocuments = async () => {
        if (!projectId) return;
        setLoading(true);
        try {
            console.log(`Fetching documents for project ${projectId}...`);
            const response = await apiClient.documents.getList(projectId.toString());
            console.log('Fetch documents response:', response);
            if (response.success) {
                setDocuments(response.data?.documents || []);
            } else {
                console.error('Fetch documents failed:', response.message);
            }
        } catch (error) {
            console.error('Failed to fetch documents:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchKgStatus = async () => {
        if (!projectId) return;
        try {
            const response = await apiClient.knowledgeGraph.getStatus(projectId.toString());
            if (response.success) {
                setKgStatus(response.data);
                // 发送知识图谱状态更新事件，供其他组件使用
                window.dispatchEvent(new CustomEvent('kgStatusUpdated', { detail: response.data }));
            }
        } catch (error) {
            console.error('Failed to fetch KG status:', error);
        }
    };

    const fetchTaskStatus = async () => {
        if (!projectId) return;
        try {
            const response = await apiClient.documentTasks.getStatus(projectId.toString());
            console.log('Task status response:', response); // 调试日志
            if (response.success) {
                // 更新文档的processing状态
                setDocuments(prev => prev.map(doc => {
                    const task = response.data.tasks?.find((t: any) =>
                        t.task_type === 'document_processing' &&
                        t.document_id === doc.id  // 修复：直接使用document_id字段
                    );
                    if (task) {
                        console.log(`Found task for document ${doc.id}:`, task); // 调试日志
                        return {
                            ...doc,
                            processing_status: task.status,
                            processing_progress: task.progress,
                            processing_error: task.error_message
                        };
                    }
                    return doc;
                }));
            }
        } catch (error) {
            console.error('Failed to fetch task status:', error);
        }
    };

    useEffect(() => {
        fetchDocuments();
        fetchKgStatus();
        fetchTaskStatus();

        // Poll for status if processing
        const interval = setInterval(() => {
            fetchKgStatus();
            fetchTaskStatus();
        }, 5000);

        // 监听全局文档刷新事件
        const handleGlobalRefresh = () => {
            console.log('Global document refresh event received in DocumentPanel');
            fetchDocuments();
            fetchKgStatus();
            fetchTaskStatus();
        };

        window.addEventListener('documentsShouldRefresh', handleGlobalRefresh);

        return () => {
            clearInterval(interval);
            window.removeEventListener('documentsShouldRefresh', handleGlobalRefresh);
        };
    }, [projectId]);

    const handleDelete = async (id: number) => {
        if (!projectId) return;
        if (!window.confirm('确定要删除此文档吗？')) return;

        try {
            await apiClient.documents.delete(projectId.toString(), id.toString());
            fetchDocuments();
            fetchKgStatus(); // Update status after deletion
        } catch (error) {
            console.error('Failed to delete document:', error);
        }
    };

    const handleRebuild = async () => {
        if (!projectId) return;
        try {
            await apiClient.knowledgeGraph.rebuild(projectId.toString());
            fetchKgStatus();
        } catch (error) {
            console.error('Failed to rebuild knowledge graph:', error);
        }
    };

    // 查看原文档
    const handleViewOriginalDocument = async (filename: string) => {
        if (!projectId) return;
        try {
            console.log('Viewing original document:', filename);
            const response = await apiClient.documents.readBlob(projectId.toString(), filename);

            // 创建blob URL用于在新标签页中查看
            const blobUrl = URL.createObjectURL(response);

            // 在新标签页中打开文档
            window.open(blobUrl, '_blank');

            // 清理blob URL以避免内存泄漏
            setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
        } catch (error) {
            console.error('Failed to view original document:', error);
            alert('无法查看文档，请稍后重试');
        }
    };

    // 查看Markdown文件
    const handleViewMarkdown = async (document: Document) => {
        if (!projectId || !document.markdown_path) {
            alert('Markdown文件不存在或路径未设置');
            return;
        }

        setLoadingMarkdown(true);
        try {
            // 从完整路径中提取文件名
            const filename = document.markdown_path.split('/').pop() || document.markdown_path;
            console.log('Viewing markdown file:', filename);
            console.log('Full path:', document.markdown_path);

            const response = await apiClient.documents.getMarkdown(projectId.toString(), filename);

            if (response.success) {
                console.log('Markdown content length:', response.data.length);
                console.log('First 100 chars:', response.data.substring(0, 100));

                // 设置模态框状态
                setMarkdownTitle(document.document_name);
                setMarkdownContent(response.data);
                setMarkdownModalOpen(true);
            } else {
                throw new Error(response.message);
            }
        } catch (error) {
            console.error('Failed to view markdown file:', error);
            alert('无法查看Markdown文件，请稍后重试');
        } finally {
            setLoadingMarkdown(false);
        }
    };


    const getStatusColor = (status: string) => {
        switch (status) {
            case 'uploaded': return 'success';
            case 'processing': return 'warning';
            case 'failed': return 'error';
            default: return 'default';
        }
    };

    const getProcessingStatusColor = (status?: string) => {
        switch (status) {
            case 'completed': return 'success';
            case 'running': return 'warning';  // 后端使用'running'状态
            case 'processing': return 'warning';  // 兼容处理
            case 'failed': return 'error';
            case 'pending': return 'info';
            case 'cancelled': return 'default';
            default: return 'default';
        }
    };

    const getProcessingStatusText = (status?: string) => {
        switch (status) {
            case 'pending': return '等待中';
            case 'running': return '转换中';  // 后端使用'running'状态
            case 'processing': return '转换中';  // 兼容处理
            case 'completed': return '已完成';
            case 'failed': return '转换失败';
            case 'cancelled': return '已取消';
            default: return '未开始';
        }
    };

    const getKgStatusText = (status?: string) => {
        switch (status) {
            case 'building': return '构建中';
            case 'completed': return '已完成';
            case 'failed': return '构建失败';
            case 'idle': return '未开始';
            case 'processing': return '处理中';
            default: return status || '未知';
        }
    };
    // 处理图数据库可视化
    const handleGraphVisualization = async () => {
        if (!projectId) return;

        setLoadingGraphVisualization(true);
        try {
            console.log('开始生成图数据库可视化');
            const response = await apiClient.get(`/projects/${projectId}/knowledge-graph/visualize`);

            if (response.success) {
                console.log('图数据库可视化生成成功');
                if (response.data.html_content && response.data.html_content.trim()) {
                    setGraphVisualizationHtml(response.data.html_content);
                    setGraphVisualizationDialogOpen(true);
                } else {
                    console.error('HTML内容为空，无法显示可视化');
                    setError('返回的可视化HTML内容为空');
                }
            } else {
                console.error('图数据库可视化生成失败:', response.message);
                setError(response.message || '图数据库可视化生成失败');
            }
        } catch (err: any) {
            console.error('图数据库可视化异常:', err);
            const errorMessage = err.response?.data?.detail || err.message || '图数据库可视化发生异常';
            setError(errorMessage);
        } finally {
            setLoadingGraphVisualization(false);
        }
    };

    return (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', borderRight: 1, borderColor: 'divider', bgcolor: 'white' }}>
            <Box sx={{ p: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: 1, borderColor: 'divider' }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>调研来源</Typography>
                <Box>
                    <Tooltip title="刷新">
                        <IconButton size="small" onClick={() => { fetchDocuments(); fetchKgStatus(); }}>
                            <RefreshIcon />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="上传文档">
                        <Button
                            variant="contained"
                            size="small"
                            startIcon={<UploadIcon />}
                            onClick={() => setUploadDialogOpen(true)}
                            sx={{ ml: 1 }}
                        >
                            上传文档
                        </Button>
                    </Tooltip>
                </Box>
            </Box>

            <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <CircularProgress size={24} />
                    </Box>
                ) : documents.length === 0 ? (
                    <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
                        <Typography variant="body2">暂无文档</Typography>
                        <Button
                            startIcon={<UploadIcon />}
                            sx={{ mt: 1 }}
                            onClick={() => setUploadDialogOpen(true)}
                        >
                            上传文档
                        </Button>
                    </Box>
                ) : (
                    <List>
                        {documents.map((doc) => (
                            <ListItem
                                key={doc.id}
                                secondaryAction={
                                    <Box>
                                        {doc.processing_status === 'completed' && doc.markdown_path && (
                                            <Tooltip title="查看Markdown文件">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleViewMarkdown(doc)}
                                                >
                                                    <EyeIcon fontSize="small" color="success" />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        <IconButton
                                            size="small"
                                            title="查看原文"
                                            onClick={() => handleViewOriginalDocument(doc.document_name)}
                                        >
                                            <FileIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton edge="end" size="small" onClick={() => handleDelete(doc.id)} title="删除">
                                            <DeleteIcon fontSize="small" color="error" />
                                        </IconButton>
                                    </Box>
                                }
                                sx={{ borderBottom: 1, borderColor: 'divider', '&:hover': { bgcolor: 'action.hover' } }}
                            >
                                <ListItemText
                                    primary={
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <FileIcon fontSize="small" color="action" />
                                            <Typography variant="body2" noWrap title={doc.document_name} sx={{ fontWeight: 500 }}>
                                                {doc.document_name}
                                            </Typography>
                                        </Box>
                                    }
                                    secondary={
                                        <Box sx={{ mt: 0.5 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                                <Typography variant="caption" color="text.secondary">
                                                    {(doc.file_size / 1024).toFixed(1)} KB
                                                </Typography>
                                            </Box>
                                            {/* MD转换状态 */}
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Chip
                                                    label={
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                            {doc.processing_status === 'running' && <ProcessingIcon sx={{ fontSize: 12 }} />}
                                                            {doc.processing_status === 'processing' && <ProcessingIcon sx={{ fontSize: 12 }} />}
                                                            {doc.processing_status === 'completed' && <CompletedIcon sx={{ fontSize: 12 }} />}
                                                            {doc.processing_status === 'failed' && <ErrorIcon sx={{ fontSize: 12 }} />}
                                                            {doc.processing_status === 'pending' && <PendingIcon sx={{ fontSize: 12 }} />}
                                                            {doc.processing_status === 'cancelled' && <PendingIcon sx={{ fontSize: 12 }} />}
                                                            {getProcessingStatusText(doc.processing_status)}
                                                        </Box>
                                                    }
                                                    size="small"
                                                    color={getProcessingStatusColor(doc.processing_status) as any}
                                                    sx={{ height: 20, fontSize: '0.7rem' }}
                                                />
                                                {(doc.processing_status === 'processing' || doc.processing_status === 'running') && doc.processing_progress && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        {doc.processing_progress}%
                                                    </Typography>
                                                )}
                                                {doc.processing_status === 'failed' && doc.processing_error && (
                                                    <Tooltip title={doc.processing_error}>
                                                        <ErrorIcon fontSize="small" color="error" sx={{ fontSize: 14 }} />
                                                    </Tooltip>
                                                )}
                                            </Box>
                                        </Box>
                                    }
                                />
                            </ListItem>
                        ))}
                    </List>
                )}
            </Box>

            {/* Knowledge Graph Construction Status */}
            <Box sx={{ borderTop: 1, borderColor: 'divider', p: 2, bgcolor: 'background.paper' }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
                    知识图谱构建状态
                </Typography>

                {/* 状态信息和进度 */}
                <Box sx={{ mb: 2 }}>
                    {kgStatus ? (
                        <Box>
                            {/* 主要状态显示 */}
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {kgStatus.status === 'processing' && <ProcessingIcon sx={{ fontSize: 16, color: 'warning.main' }} />}
                                    {kgStatus.status === 'completed' && <CompletedIcon sx={{ fontSize: 16, color: 'success.main' }} />}
                                    {kgStatus.status === 'failed' && <ErrorIcon sx={{ fontSize: 16, color: 'error.main' }} />}
                                    {kgStatus.status === 'idle' && <PendingIcon sx={{ fontSize: 16, color: 'info.main' }} />}
                                    <Chip
                                        label={getKgStatusText(kgStatus.status)}
                                        size="small"
                                        color={
                                            kgStatus.status === 'processing' ? 'warning' :
                                                kgStatus.status === 'completed' ? 'success' :
                                                    kgStatus.status === 'failed' ? 'error' : 'info'
                                        }
                                        sx={{ height: 22, fontSize: '0.7rem' }}
                                    />
                                </Box>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        sx={{ minWidth: 'auto', px: 1, py: 0.5, fontSize: '0.75rem' }}
                                        disabled={kgStatus.status === 'processing'}
                                        onClick={handleGraphVisualization}
                                    >
                                        预览
                                    </Button>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        sx={{ minWidth: 'auto', px: 1, py: 0.5, fontSize: '0.75rem' }}
                                        onClick={handleRebuild}
                                        disabled={kgStatus.status === 'processing'}
                                    >
                                        重建
                                    </Button>
                                </Box>
                            </Box>

                            {/* 详细信息 */}
                            {kgStatus.status === 'processing' && (
                                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                                    正在处理文档: {kgStatus.processed_documents || 0}/{kgStatus.total_documents || 0}
                                </Typography>
                            )}

                            {kgStatus.status === 'completed' && (
                                <Typography variant="caption" color="success.main" sx={{ mb: 1, display: 'block' }}>
                                    已成功处理 {kgStatus.total_documents || 0} 个文档
                                </Typography>
                            )}
                        </Box>
                    ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CircularProgress size={16} />
                            <Typography variant="caption" color="text.secondary">
                                加载中...
                            </Typography>
                        </Box>
                    )}
                </Box>

                {/* 进度条 */}
                <Box sx={{ mb: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">
                            构建进度
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {kgStatus?.progress || 0}%
                        </Typography>
                    </Box>
                    <Box sx={{ width: '100%', bgcolor: 'action.hover', borderRadius: 1, height: 8 }}>
                        <Box
                            sx={{
                                width: `${kgStatus?.progress || 0}%`,
                                bgcolor: kgStatus?.status === 'processing' ? 'warning.main' :
                                    kgStatus?.status === 'completed' ? 'success.main' :
                                        kgStatus?.status === 'failed' ? 'error.main' : 'grey.400',
                                borderRadius: 1,
                                height: 8,
                                transition: 'width 0.3s ease'
                            }}
                        />
                    </Box>
                </Box>
                <Dialog
                    open={graphVisualizationDialogOpen}
                    onClose={() => setGraphVisualizationDialogOpen(false)}
                    maxWidth="xl"
                    fullWidth
                    PaperProps={{
                        sx: {
                            height: '90vh',
                            maxHeight: '90vh'
                        }
                    }}
                >
                    <DialogTitle sx={{ pb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <GraphIcon color="primary" />
                        知识图谱可视化
                    </DialogTitle>
                    <DialogContent dividers sx={{ padding: 0, height: 'calc(90vh - 120px)', overflow: 'hidden' }}>
                        {graphVisualizationHtml ? (
                            <iframe
                                srcDoc={graphVisualizationHtml}
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    border: 'none',
                                    borderRadius: 0
                                }}
                                title="知识图谱可视化"
                            />
                        ) : (
                            <Box sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                height: '100%',
                                flexDirection: 'column',
                                gap: 2
                            }}>
                                <CircularProgress size={48} />
                                <Typography variant="body1" color="text.secondary">
                                    正在生成知识图谱可视化...
                                </Typography>
                            </Box>
                        )}
                    </DialogContent>
                    <DialogActions sx={{ px: 3, py: 2 }}>
                        <Button
                            onClick={() => setGraphVisualizationDialogOpen(false)}
                            variant="outlined"
                        >
                            关闭
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>

            {/* Markdown查看模态框 */}
            <Dialog
                open={markdownModalOpen}
                onClose={() => setMarkdownModalOpen(false)}
                maxWidth="lg"
                fullWidth
                PaperProps={{
                    sx: {
                        height: '80vh',
                        maxHeight: '800px'
                    }
                }}
            >
                <DialogTitle>
                    <Typography variant="h6" component="div">
                        {markdownTitle}
                    </Typography>
                </DialogTitle>
                <DialogContent sx={{ p: 0 }}>
                    {loadingMarkdown ? (
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '300px',
                            flexDirection: 'column',
                            gap: 2
                        }}>
                            <CircularProgress size={48} />
                            <Typography variant="body1" color="text.secondary">
                                正在加载Markdown文件...
                            </Typography>
                        </Box>
                    ) : (
                        <Box sx={{
                            height: '100%',
                            overflow: 'auto',
                            p: 3,
                            backgroundColor: '#fafafa'
                        }}>
                            <Box
                                sx={{
                                    backgroundColor: '#fff',
                                    borderRadius: 1,
                                    p: 3,
                                    boxShadow: 1,
                                    '& .markdown-content': {
                                        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Arial, sans-serif',
                                        fontSize: '16px',
                                        lineHeight: 1.6,
                                        color: '#333',
                                        '& h1': {
                                            fontSize: '2rem',
                                            fontWeight: 'bold',
                                            marginBottom: '1rem',
                                            color: '#1976d2',
                                            borderBottom: '2px solid #e3f2fd',
                                            paddingBottom: '0.5rem'
                                        },
                                        '& h2': {
                                            fontSize: '1.5rem',
                                            fontWeight: 'bold',
                                            marginBottom: '0.75rem',
                                            color: '#1976d2',
                                            marginTop: '2rem'
                                        },
                                        '& h3': {
                                            fontSize: '1.25rem',
                                            fontWeight: 'bold',
                                            marginBottom: '0.5rem',
                                            color: '#424242',
                                            marginTop: '1.5rem'
                                        },
                                        '& p': {
                                            marginBottom: '1rem',
                                            lineHeight: 1.6
                                        },
                                        '& ul, & ol': {
                                            marginBottom: '1rem',
                                            paddingLeft: '1.5rem'
                                        },
                                        '& li': {
                                            marginBottom: '0.25rem',
                                            lineHeight: 1.5
                                        },
                                        '& blockquote': {
                                            borderLeft: '4px solid #1976d2',
                                            paddingLeft: '1rem',
                                            paddingTop: '0.5rem',
                                            paddingBottom: '0.5rem',
                                            marginTop: '1rem',
                                            marginBottom: '1rem',
                                            backgroundColor: '#f5f5f5',
                                            fontStyle: 'italic'
                                        },
                                        '& code': {
                                            backgroundColor: '#f5f5f5',
                                            padding: '2px 4px',
                                            borderRadius: '4px',
                                            fontSize: '0.875em',
                                            fontFamily: 'Monaco, Menlo, Ubuntu Mono, monospace'
                                        },
                                        '& pre': {
                                            backgroundColor: '#263238',
                                            color: '#fff',
                                            padding: '1rem',
                                            borderRadius: '4px',
                                            overflowX: 'auto',
                                            marginBottom: '1rem',
                                            '& code': {
                                                backgroundColor: 'transparent',
                                                padding: 0
                                            }
                                        },
                                        '& table': {
                                            width: '100%',
                                            borderCollapse: 'collapse',
                                            marginBottom: '1rem'
                                        },
                                        '& th, & td': {
                                            border: '1px solid #e0e0e0',
                                            padding: '8px 12px',
                                            textAlign: 'left'
                                        },
                                        '& th': {
                                            backgroundColor: '#f5f5f5',
                                            fontWeight: 'bold'
                                        },
                                        '& a': {
                                            color: '#1976d2',
                                            textDecoration: 'none',
                                            '&:hover': {
                                                textDecoration: 'underline'
                                            }
                                        },
                                        '& img': {
                                            maxWidth: '100%',
                                            height: 'auto',
                                            borderRadius: '4px',
                                            marginTop: '1rem',
                                            marginBottom: '1rem'
                                        }
                                    }
                                }}
                                className="markdown-content"
                            >
                                <ReactMarkdown
                                    remarkPlugins={[remarkGfm]}
                                    rehypePlugins={[rehypeRaw]}
                                >
                                    {markdownContent}
                                </ReactMarkdown>
                            </Box>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button
                        onClick={() => setMarkdownModalOpen(false)}
                        variant="outlined"
                    >
                        关闭
                    </Button>
                </DialogActions>
            </Dialog>

            <DocumentUploadDialog
                open={uploadDialogOpen}
                onClose={() => {
                    setUploadDialogOpen(false);
                    // 关闭弹窗时也刷新文档列表，以防有上传中的文件或新文件
                    fetchDocuments();
                    fetchKgStatus();
                }}
                onUploadComplete={() => {
                    console.log('Upload complete, refreshing documents...');
                    setUploadDialogOpen(false);
                    fetchDocuments();
                    fetchKgStatus();
                    // 触发全局文档刷新事件，通知其他页面更新文档列表
                    window.dispatchEvent(new CustomEvent('documentsShouldRefresh'));
                }}
                projectId={projectId ? projectId.toString() : ''}
            />
        </Box>
    );
};

export default DocumentPanel;
